import Dce from "dce-engine/lib/decorators/Dce";
import DceComponent from "dce-engine/lib/engine/DceComponent";
import DceHTMLElement from "dce-engine/lib/types/DceHTMLElement";
import FragmentWireEventListener from "./FragmentWireEventListener";
import {DceAjax, DceAjaxResponse} from "dce-ajax";
import {AjaxResp, Events, FragmentAction, KnownActionData} from "./ajax/AjaxResponse";
import DceEngineEventDetails from "../../../dce-engine/lib/engine/events/DceEngineEventDetails";
import DceEngineEvents from "../../../dce-engine/lib/engine/events/DceEngineEvents";
import ModalFragment from "./ModalFragment";
import {DceEngine} from "dce-engine";
import {DomCommands} from "./ajax/DomManipulation";
import {urlSafeBase64AsJson} from "./utils/base64";
import {DceWebSocketConnection, DceWebSocketIncomingMessage, DceWebSocketSubscribeHandle} from "dce-websocket";
import wireActionParse, {ParsedWireAction} from "./utils/wireActionParse";
import serializeFormInputs from "./utils/serializeFormInputs";
import DceWireEventDetails from "./dceplugin/DceWireEventDetails";

export interface FragmentProps {
    //klasa po stronie serwera
    c: string,

    //propertiesy z serwera
    p: string,

    //tagi do określenia roli fragmentu
    tags: string,

    //kanał na WS na którym fragment nasłuchuje na swoje komendy
    wsChannel?: string,

    defChannel?: string
}

@Dce("Fragment")
export default class Fragment extends DceComponent<FragmentProps> {

    private parent: Fragment;

    private wireEvents: FragmentWireEventListener;

    private calls: Map<string, (a: FragmentAction<keyof KnownActionData>[]) => void>
        = new Map<string, (a: FragmentAction<keyof KnownActionData>[]) => void>();

    private websocketCalls: Map<string, (msg: string) => void> = new Map<string, (msg: string) => void>();

    private websocketSubscribeHandle: DceWebSocketSubscribeHandle = null;
    private defWebsocketSubscribeHandle: DceWebSocketSubscribeHandle = null;

    private loadingElement: Array<[HTMLElement, string]> = [];
    private loadingCounter = 0;

    public wireEventsLocks: Map<string, boolean> = new Map<string, boolean>();

    public formDataSerializer: (elem: HTMLElement) => FormData;

    constructor(elem: DceHTMLElement, props: FragmentProps) {
        super(elem, props);

        Events.addEventListeners(
            this.elem,
            async (e) => {
                this.onOpenModalEvent(e);
            },
            (e) => {
                this.onDom(e);
            },
            (e) => {
                this.onReplaceFragmentViewEvent(e);
            },
            (e) => {
                this.onReplaceFragmentEvent(e);
            },
            async (e) => {
                this.onServerEvent(e);
            },
            e => {
                this.onUiEvent(e);
            },
            e => {
                console.log("UPDATE PROPS: ", e.detail.p);
                this.props.p = e.detail.p;
            }
        );

        this.findLoadingElement();

        this.wireEvents = new FragmentWireEventListener(this.elem, this);
        this.registerCalls();

        this.registerWebSocketChannel();
    }

    public setFormDataSerializer(serializer: (elem: HTMLElement) => FormData){
        this.formDataSerializer = serializer;
    }

    public serializeForm(elem: HTMLElement): FormData{
        if(null == this.formDataSerializer){
            return new FormData(elem as HTMLFormElement);
        }
        return this.formDataSerializer(elem);
    }

    private hasTag(tag: string): boolean {

        if (null == tag) {
            return true;
        }

        return this.props.tags.indexOf("/" + tag + "/") > -1;
    }

    public registerCalls() {
        //add custom calls registers
    }

    public registerCall(call: string, func: ((a: FragmentAction<keyof KnownActionData>[]) => void)) {
        this.calls.set(call, func);
    }

    public onUiEvent(e: CustomEvent<KnownActionData['uievent']>) {
        console.log("EEEEEE UIENVENT", e, this.elem);
        this.dispatchSimpleEvent(e.detail.e);
    }

    private async callEvent(event: string, eventParam: string) {
        this.showLoading();
        let data = await DceAjax.getInstance().get(
            this.generateEventUrl(event, eventParam)
        );

        setTimeout(() => {
            this.hideLoading();
            this.processResponse(data);
        });
    }

    public async onServerEvent(e: CustomEvent<KnownActionData["event"]>) {

        //nie mam taga, to wywołujemy na parencie aż znajdziemy
        if (!this.hasTag(e.detail.tag)) {
            if (this.parent) {
                this.parent.onServerEvent(e);
            }
            return;
        }

        this.callEvent(e.detail.e, e.detail.param);
    }

    public async onOpenModalEvent(e: CustomEvent<KnownActionData["openModal"]>) {
        let data = await this.openModal(e);
        this.processActions(data);
    }

    public async openModal(
        e: CustomEvent<KnownActionData["openModal"]>
    ): Promise<FragmentAction<keyof KnownActionData>[]> {

        return new Promise(((resolve, reject) => {

            let data = e.detail;

            let div = document.createElement("div");
            div.innerHTML = data.html;

            div.addEventListener('dceEvent', (e: CustomEvent<DceEngineEventDetails>) => {

                if (e.detail.type == DceEngineEvents.SUBCOMP_INIT_EVENT && e.detail.component == 'ModalFragment') {

                    let modalElement = e.detail.element;
                    let modalFragment = modalElement.ext.getComponent('ModalFragment') as ModalFragment;

                    modalFragment.onPassToOpener(e => {
                        this.processActions(e);
                    });

                    modalFragment.onClose(actions => {
                        resolve(actions);
                    });

                }

            });

            DceEngine.getInstance().watchElement(div);

            let target = document.querySelector(data.target);
            if (target) {
                target.append(div);
            } else {
                document.body.append(div);
            }

        }));

    }


    public onReplaceFragmentEvent(e: CustomEvent<KnownActionData["rf"]>) {
        let div = document.createElement('div');
        div.innerHTML = e.detail.html;
        this.elem.replaceWith(div.firstElementChild);
    }

    public onReplaceFragmentViewEvent(e: CustomEvent<KnownActionData["rfv"]>) {
        let div = document.createElement('div');
        div.innerHTML = e.detail.html;

        this.elem.innerHTML = div.firstElementChild.innerHTML;

        setTimeout(() => {
            this.findLoadingElement();
        }, 10);

    }

    public onDom(e: CustomEvent<KnownActionData["dom"]>) {

        if (e.detail.tag && !this.hasTag(e.detail.tag)) {
            if (this.parent) {
                this.parent.onDom(e);
            }
            return;
        }

        let selector = e.detail.selector;
        let dm = e.detail.commands;

        for (const dmElement of dm) {
            DomCommands.execute(this.elem, selector, dmElement.command, dmElement.data);
        }

    }


    public async call(action: string, params: any, preventLoading: boolean = false): Promise<DceAjaxResponse> {

        let encParams = urlSafeBase64AsJson(params);

        if (!preventLoading) {
            this.showLoading('');
        }

        return DceAjax.getInstance().get(
            this.generateCallUrl(action, encParams)
        ).then(res => {
            if (!preventLoading) {
                this.hideLoading('');
            }
            return res;
        });

    }


    public async callPost(wireAction: string, data: any, preventLoading: boolean = false): Promise<DceAjaxResponse> {

        let parsed = wireActionParse('click', wireAction);

        if (!preventLoading) {
            this.showLoading(parsed.loadingTag);
        }

        return DceAjax.getInstance().post(
            this.generateCallUrl(parsed.action, parsed.params),
            data
        ).then(res => {
            if (!preventLoading) {
                this.hideLoading(parsed.loadingTag);
            }
            return res;
        });

    }


    public async callWireAction(wireAction: string, preventLoading: boolean = false): Promise<DceAjaxResponse> {

        let parsed = wireActionParse('click', wireAction);

        if (!preventLoading) {
            this.showLoading(parsed.loadingTag);
        }

        return DceAjax.getInstance().get(
            this.generateCallUrl(parsed.action, parsed.params)
        ).then(res => {
            if (!preventLoading) {
                this.hideLoading(parsed.loadingTag);
            }
            return res;
        });

    }

    public async action(action: string, params: any) {
        let encParams = urlSafeBase64AsJson(params);
        let url = this.generateCallUrl(action, encParams);

        this.showLoading('');
        let data = await DceAjax.getInstance().get(url);

        setTimeout(() => {
            this.hideLoading('');
            this.processResponse(data);
        });
    }


    public async callActionPost(wireAction: string, postData: any) {

        let parsed = wireActionParse('click', wireAction);

        this.showLoading('');
        let data = await DceAjax.getInstance().post(
            this.generateCallUrl(parsed.action, parsed.params), postData
        );

        setTimeout(() => {
            this.hideLoading('');
            this.processResponse(data);
        });
    }

    private generateCallUrl(action: string, params: string, rawGet: string = ''): string {

        let url: string[] = ["f", "call", this.props.c, this.props.p, action, params];

        let path = "/" + url.map(v => encodeURIComponent(v)).join("/");

        if (rawGet) {
            path += "?" + rawGet;
        }

        return path;
    }

    private generateEventUrl(event: string, param: string): string {
        let url: string[] = ["f", "event", this.props.c, this.props.p, event];

        if (null != param) {
            url.push(param);
        }

        return "/" + url.map(v => encodeURIComponent(v)).join("/");
    }


    public async executeWireEvent(type: string, action: ParsedWireAction, elem: HTMLElement) {

        if (this.wireEventsLocks.has(type) && this.wireEventsLocks.get(type)) {
            return;
        }

        if (action.fragmentTag.length > 0) {
            if (!this.hasTag(action.fragmentTag)) {
                if (null != this.parent) {
                    await this.parent.executeWireEvent(type, action, elem);
                }

                this.wireEventsLocks.set(type, false);
                return;
            }
        }

        this.wireEventsLocks.set(type, true);

        let formData = new FormData();

        if (action.serializeSelector != '') {
            let form = elem.closest(action.serializeSelector);
            formData = serializeFormInputs(form as HTMLElement)
        } else {
            if (type == 'submit') {
                //formData = new FormData(elem as HTMLFormElement);
                formData = this.serializeForm(elem as HTMLFormElement);
            }
        }

        if (action.formData) {
            action.formData.forEach(
                (value, key) => {
                    formData.append(key, value);
                }
            );
        }

        let url = this.generateCallUrl(action.action, action.params, action.rawGet);

        if (action.method == 'get') {

            let gets: string[] = [];
            formData.forEach((value, key) => {
                gets.push(
                    key + '=' + encodeURIComponent(value.toString())
                );
            })

            if (gets.length > 0) {

                if (url.indexOf('?') > 0) {
                    url += '?';
                } else {
                    url += '&';
                }

                url += gets.join("&");
            }

            this.showLoading(action.loadingTag);

            let data = await DceAjax.getInstance().get(url);

            setTimeout(() => {
                this.hideLoading(action.loadingTag);
                this.processResponse(data);
            });

        }

        if (action.method == 'post') {
            this.showLoading(action.loadingTag);

            if(type == 'submit') {

                setTimeout(() => {
                    elem.dispatchEvent(new CustomEvent<any>('dceWireSubmit', {
                            bubbles: false,
                            detail: {
                                type: 'submit'
                            }
                        }),
                    );
                }, 0);

            }

            let data = await DceAjax.getInstance().post(
                url, formData
            );

            this.hideLoading(action.loadingTag);

            setTimeout(() => {

                this.processResponse(data);
            });
        }

        this.wireEventsLocks.set(type, false);
    }

    private processResponse(resp: DceAjaxResponse) {

        let actionresp = resp.rawData() as AjaxResp<keyof KnownActionData>;

        if (actionresp.fragmentActions) {
            this.processActions(actionresp.fragmentActions);
        }

        if (actionresp.fragmentCalls) {
            for (const calldata of actionresp.fragmentCalls) {

                let call = calldata.method;
                let data = calldata.actions;

                if (this.calls.has(call)) {
                    (this.calls.get(call))(data);
                }
            }
        }
    }

    protected processActions(actions: FragmentAction<keyof KnownActionData>[]) {
        for (const action of actions) {
            Events.dispatch(this.elem, action.type, action.data);
        }
    }

    public setParentFragment(parent: Fragment) {
        this.parent = parent;
    }

    onSubComponentInit(component: string, elem: DceHTMLElement) {
        super.onSubComponentInit(component, elem);

        if (elem.ext.hasComponent("Fragment")) {

            this.loadingElement = this.loadingElement.filter(v => {
                return !elem.contains(v[0]);
            });

            (elem.ext.getComponent("Fragment") as Fragment).setParentFragment(this);
        }

        if (elem.ext.hasComponent("ModalFragment")) {

            this.loadingElement = this.loadingElement.filter(v => {
                return !elem.contains(v[0]);
            });

            (elem.ext.getComponent("ModalFragment") as Fragment).setParentFragment(this);
        }


    }


    private registerWebSocketChannel() {
        if (this.props.wsChannel) {
            this.websocketSubscribeHandle = DceWebSocketConnection.getInstance()
                .subscribe(this.props.wsChannel, async (msg) => {
                    this.onWebSocketMessage(msg);
                });
        }

        if (this.props.defChannel) {
            this.defWebsocketSubscribeHandle = DceWebSocketConnection.getInstance()
                .subscribe(this.props.defChannel, async (msg) => {
                    this.onWebSocketMessage(msg);
                });
        }
    }

    private callWebSocket(method: string, param: string) {

    }

    private async onWebSocketMessage(msg: DceWebSocketIncomingMessage) {

        if (msg.message.startsWith("$")) {
            let x = msg.message.indexOf("|");

            let method = msg.message.substr(1, x - 1);
            let msgstr = msg.message.substr(x + 1);

            console.log("EVENT METHOD: ", method, msgstr);

            await this.callEvent(method, msgstr);
            return;
        }

        if (msg.message == 'remove') {
            this.elem.parentElement.removeChild(this.elem);
        }

        if (msg.message == 'refresh') {

            this.showLoading();
            let data = await DceAjax.getInstance().get(
                this.generateEventUrl("_refresh", null)
            );

            setTimeout(() => {
                this.hideLoading();
                this.processResponse(data);
            });

        }


        if (msg.message.startsWith("#")) {
            let x = msg.message.indexOf(">");

            let method = msg.message.substr(1, x - 1);
            let msgstr = msg.message.substr(x + 1);

            console.log("WEBSOCKET METHOD: ", method, msgstr);

            this.callWebSocket(method, msgstr);
        }

        if (msg.message.startsWith("@")) {

            let jsonPart = msg.message.substring(1);
            let data = JSON.parse(jsonPart);

            //powinna być obsługa : KnownActionData["dom"]
            //TODO: poprawna obłśuga teraz skrót

            console.log("_____________", data);

            let target = this.elem.querySelector(data.selector);

            let tmp = document.createElement("div");
            tmp.innerHTML = data.html;
            target.prepend(
                tmp.firstElementChild
            );

        }

    }

    onRemove() {
        if (null != this.websocketSubscribeHandle) {
            DceWebSocketConnection.getInstance().unsubscribe(this.websocketSubscribeHandle);
        }

        if (null != this.defWebsocketSubscribeHandle) {
            DceWebSocketConnection.getInstance().unsubscribe(this.defWebsocketSubscribeHandle);
        }
    }

    private findLoadingElement() {
        let foundElements = this.elem.querySelectorAll('*[data-wire-loading]');

        if (foundElements.length > 0) {
            this.loadingElement = new Array<[HTMLElement, string]>();
            foundElements.forEach(e => {
                let display = e.getAttribute('data-wire-loading');
                this.loadingElement.push(
                    [e as HTMLElement, display]
                );
            });
        }
    }

    private showLoading(tag: string = '') {

        if (tag === '' || this.hasTag(tag)) {
            this.loadingCounter++;

            this.loadingElement.forEach((d, index) => {
                d[0].style.display = d[1];
            });

            return;
        }

        if (null != this.parent) {
            this.parent.showLoading(tag);
        }

    }

    private hideLoading(tag: string = '') {

        if (tag === '' || this.hasTag(tag)) {

            this.loadingCounter--;

            if (this.loadingCounter == 0) {
                this.loadingElement.forEach((d, index) => {
                    d[0].style.display = 'none';
                });
            }

            return;
        }

        if (null != this.parent) {
            this.parent.hideLoading(tag);
        }

    }


    //region ui update methods

    public update(selector: string, value: Partial<any>) {

        if (!selector) {
            return;
        }

        this.elem.querySelectorAll(selector).forEach((v) => {
            Object.assign(v, value);
        });

    }

    public updateDisplay(selector: string, value: string) {

        if (!selector) {
            return;
        }

        this.elem.querySelectorAll(selector).forEach((v) => {
            (v as HTMLElement).style.display = value;
        });

    }


    //endregion

}
